import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { debug as debugFn } from "debug";
import { useTranslation } from "react-i18next";

import type { ThalamosNotification } from "@thalamos/vision-common";
import { NotificationBodyTypeSchema } from "@thalamos/vision-common";

import api, { apiHooks } from "../api/index.js";
import { useToastNotifications } from "../hooks/useToastNotifications.js";
import type { PolicePresenceRequestedModalProps } from "../modals/PolicePresenceRequested/PolicePresenceRequested.js";

type NotificationContextType = {
  reloadNotifications: () => void;
  notifications: ThalamosNotification[];
  transferRequested: {
    isOpen: boolean;
    data: ThalamosNotification | null;
    open: (data: ThalamosNotification) => void;
    close: () => void;
  };
  transferRejected: {
    isOpen: boolean;
    data: ThalamosNotification | null;
    open: (data: ThalamosNotification) => void;
    close: () => void;
  };
  s136FormRejected: {
    isOpen: boolean;
    data: ThalamosNotification | null;
    open: (data: ThalamosNotification) => void;
    close: () => void;
  };
  policePresenceRequested: {
    isOpen: boolean;
    data: ThalamosNotification | null;
    open: (
      data: ThalamosNotification,
      navigateTo: PolicePresenceRequestedModalProps["navigateTo"],
    ) => void;
    close: () => void;
  };

  // NHS side responding to the police presence request
  policePresenceUpdates: {
    isOpen: boolean;
    data: ThalamosNotification | null;
    open: (data: ThalamosNotification) => void;
    close: () => void;
  };
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider = ({
  children,
  userId,
}: {
  children: ReactNode;
  userId: string;
}) => {
  const [{ data }, reloadNotifications] = apiHooks.notifications.unread(userId);

  const notifications = data && data.length > 0 ? data : [];

  return (
    <NotificationProviderInner
      notifications={notifications}
      reloadNotifications={() => void reloadNotifications()}
    >
      {children}
    </NotificationProviderInner>
  );
};

export const NotificationProviderInner = ({
  children,
  notifications,
  reloadNotifications,
}: {
  children: ReactNode;
  notifications: ThalamosNotification[];
  reloadNotifications: () => void;
}) => {
  const { t } = useTranslation();
  const toastNotifications = useToastNotifications();

  const [transferRequested, setTransferRequested] = useState<{
    isOpen: boolean;
    data: ThalamosNotification | null;
  }>({
    isOpen: false,
    data: null,
  });

  const [transferRejected, setTransferRejected] = useState<{
    isOpen: boolean;
    data: ThalamosNotification | null;
  }>({
    isOpen: false,
    data: null,
  });

  const [s136FormRejected, setS136FormRejected] = useState<{
    isOpen: boolean;
    data: ThalamosNotification | null;
  }>({
    isOpen: false,
    data: null,
  });

  const [policePresenceRequested, setPolicePresenceRequested] = useState<{
    isOpen: boolean;
    data: ThalamosNotification | null;
    navigateTo: PolicePresenceRequestedModalProps["navigateTo"];
  }>({
    isOpen: false,
    data: null,
    navigateTo: "formProgressPage",
  });

  const [policePresenceUpdates, setPolicePresenceUpdates] = useState<{
    isOpen: boolean;
    data: ThalamosNotification | null;
  }>({
    isOpen: false,
    data: null,
  });

  const debug = debugFn("vision-frontend:Notifications");

  useEffect(() => {
    const notification = notifications[0];
    if (notification !== undefined) {
      handleNotification(notification);
    }

    // Reset the state of the modals if the notification is not in the list
    if (
      transferRequested.data?.id &&
      !notifications.some((n) => n.id === transferRequested.data?.id)
    ) {
      setTransferRequested({ isOpen: false, data: null });
    }

    if (
      transferRejected.data?.id &&
      !notifications.some((n) => n.id === transferRejected.data?.id)
    ) {
      setTransferRejected({ isOpen: false, data: null });
    }

    if (
      s136FormRejected.data?.id &&
      !notifications.some((n) => n.id === s136FormRejected.data?.id)
    ) {
      setS136FormRejected({ isOpen: false, data: null });
    }

    if (
      policePresenceRequested.data?.id &&
      !notifications.some((n) => n.id === policePresenceRequested.data?.id)
    ) {
      setPolicePresenceRequested({
        isOpen: false,
        data: null,
        navigateTo: "formProgressPage",
      });
    }

    if (
      policePresenceUpdates.data?.id &&
      !notifications.some((n) => n.id === policePresenceUpdates.data?.id)
    ) {
      setPolicePresenceUpdates({
        isOpen: false,
        data: null,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const handleNotification = (notification: ThalamosNotification) => {
    switch (notification.text.type) {
      case NotificationBodyTypeSchema.enum.transferRequested:
        setTransferRequested({ isOpen: true, data: notification });
        break;
      case NotificationBodyTypeSchema.enum.transferRejected:
        setTransferRejected({ isOpen: true, data: notification });
        break;
      case NotificationBodyTypeSchema.enum.s136FormAccepted:
        api.notifications
          .markAsRead(notification.id)
          .then(() => {
            toastNotifications.show(t("notification.s136Accepted"), {
              severity: "success",
            });
          })
          .catch(() => {
            toastNotifications.show(t("notifications.failedMarkAsRead"), {
              severity: "error",
            });
          });
        break;
      case NotificationBodyTypeSchema.enum.s136FormRejected:
        setS136FormRejected({ isOpen: true, data: notification });
        break;
      case NotificationBodyTypeSchema.enum.policePresenceRequested:
        setPolicePresenceRequested({
          isOpen: true,
          data: notification,
          navigateTo: "formProgressPage",
        });
        setPolicePresenceUpdates({
          isOpen: true,
          data: notification,
        });
        break;
      default:
        debug("Unhandled notification type", notification.text.type);
        api.notifications.markAsRead(notification.id).catch(() => {
          toastNotifications.show(t("notifications.failedMarkAsRead"), {
            severity: "error",
          });
        });
        break;
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        reloadNotifications,
        notifications: notifications,
        transferRequested: {
          ...transferRequested,
          open: (data) => {
            setTransferRequested({ isOpen: true, data });
          },
          close: () => {
            setTransferRequested({ isOpen: false, data: null });
          },
        },
        transferRejected: {
          ...transferRejected,
          open: (data) => {
            setTransferRejected({ isOpen: true, data });
          },
          close: () => {
            setTransferRejected({ isOpen: false, data: null });
          },
        },
        s136FormRejected: {
          ...s136FormRejected,
          open: (data) => {
            setS136FormRejected({ isOpen: true, data });
          },
          close: () => {
            setS136FormRejected({ isOpen: false, data: null });
          },
        },
        policePresenceRequested: {
          ...policePresenceRequested,
          open: (data, navigateTo) => {
            setPolicePresenceRequested({ isOpen: true, data, navigateTo });
          },
          close: () => {
            setPolicePresenceRequested({
              isOpen: false,
              data: null,
              navigateTo: "formProgressPage",
            });
          },
        },

        policePresenceUpdates: {
          ...policePresenceUpdates,
          open: (data) => {
            setPolicePresenceUpdates({ isOpen: true, data });
          },
          close: () => {
            setPolicePresenceUpdates({ isOpen: false, data: null });
          },
        },
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};
